import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faMoneyBillWave,
  faEdit,
  faCalendarPlus,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";

const EditCapitalModal = ({ capitalEntry, isOpen, onClose, onUpdate }) => {
  const [form, setForm] = useState({
    amount: "",
    date: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (capitalEntry) {
      const amountValue = capitalEntry.amount || capitalEntry.capital_amount;
      const dateValue =
        capitalEntry.date || capitalEntry.added_date || capitalEntry.created_at;

      setForm({
        amount: amountValue?.toString() || "",
        date: dateValue ? new Date(dateValue).toISOString().split("T")[0] : "",
      });
      setErrors({});
    }
  }, [capitalEntry]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!form.amount || parseFloat(form.amount) <= 0) {
      newErrors.amount = "Please enter a valid capital amount greater than 0";
    }

    if (!form.date) {
      newErrors.date = "Please select a date";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      await onUpdate({
        capitalId: capitalEntry.capitalId || capitalEntry.id,
        amount: parseFloat(form.amount),
        date: form.date,
      });
      setForm({ amount: "", date: "" });
      setErrors({});
      onClose();
    } catch (error) {
      console.error("Failed to update capital:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setForm({ amount: "", date: "" });
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Capital Contribution"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-gray-300 hover:bg-gray-100 text-gray-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Updating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Update Capital
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faEdit} className="text-blue-700 h-4 w-4" />
          </div>
          <div>
            <p className="text-xs text-gray-700 font-semibold">
              Edit Capital Contribution
            </p>
            <p className="text-xs text-gray-600">
              Update capital amount and date
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-green-700 h-3.5 w-3.5"
            />
            Capital Amount *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-gray-600 text-sm font-medium">TZS</span>
            </div>
            <Input
              name="amount"
              type="number"
              min="0"
              step="1000"
              value={form.amount}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="Enter capital amount"
              className={`pl-14 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30 border ${
                errors.amount ? "border-red-500" : "border-gray-300"
              } rounded-md`}
            />
          </div>
          {errors.amount && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.amount}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarPlus}
              className="text-blue-700 h-3.5 w-3.5"
            />
            Date *
          </label>
          <Input
            name="date"
            type="date"
            value={form.date}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30 border ${
              errors.date ? "border-red-500" : "border-gray-300"
            } rounded-md px-3`}
          />
          {errors.date && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.date}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditCapitalModal;
